/**
 * AssetLibraryPanel — Asset-Library-Seitenpanel für Drag & Drop bzw. Klick-
 * zum-Hinzufügen in Channels. Theme-aware (Mock #10). Header/Suche/Filter in
 * AssetLibraryControls, Grid/Thumbnail/Gruppierung in AssetLibraryGrid.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getImageCollection } from '@/lib/api/image-collection';
import type { ProductImageAsset } from '@/types/image-collection';
import { EID } from '@/lib/element-ids.registry';
import { SessionGroup, DraggableAssetThumbnail, buildGroups } from './AssetLibraryGrid';
import { AssetLibraryControls, type SourceFilter } from './AssetLibraryControls';

interface AssetLibraryPanelProps {
  productId: string;
  onClose: () => void;
  /** Per-Channel-„+": Library kanal-kontextualisiert; Klick fügt zum Channel hinzu. */
  targetChannel?: { id: string; name: string } | null;
  onAddAsset?: (asset: ProductImageAsset) => void;
}

export function AssetLibraryPanel({ productId, onClose, targetChannel, onAddAsset }: AssetLibraryPanelProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [page, setPage] = useState(1);
  const [showGrouped, setShowGrouped] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set(['today', 'yesterday']));

  const apiFilters = useMemo(() => {
    const filters: Record<string, string | undefined> = { page: page.toString(), per_page: '24' };
    if (sourceFilter !== 'all') filters.source = sourceFilter;
    return filters;
  }, [sourceFilter, page]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['asset-library', productId, apiFilters],
    queryFn: () => getImageCollection(productId, apiFilters),
    enabled: !!productId,
    staleTime: 30000,
  });

  const assets = useMemo(() => data?.images || [], [data?.images]);
  const totalCount = data?.total_count || 0;
  const totalPages = data?.total_pages || 1;

  // Deduplicate by content_hash (same image linked to multiple channels)
  const uniqueAssets = useMemo(() => {
    const seen = new Set<string>();
    return assets.filter((a) => {
      const key = a.content_hash || a.asset_id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [assets]);

  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return uniqueAssets;
    const q = searchQuery.toLowerCase();
    return uniqueAssets.filter((a) => {
      const altText = a.alt_text?.toLowerCase() || '';
      const tags = a.tags?.map((t2) => t2.name.toLowerCase()).join(' ') || '';
      return altText.includes(q) || tags.includes(q);
    });
  }, [uniqueAssets, searchQuery]);

  const groupedAssets = useMemo(() => buildGroups(filteredAssets), [filteredAssets]);

  const toggleSession = useCallback((sessionKey: string) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionKey)) next.delete(sessionKey);
      else next.add(sessionKey);
      return next;
    });
  }, []);

  const handleFilterChange = useCallback((filter: SourceFilter) => {
    setSourceFilter(filter);
    setPage(1);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const onAdd = targetChannel && onAddAsset ? onAddAsset : undefined;

  return (
    <aside id={EID['product-images-library-panel']} className="fixed right-0 top-16 bottom-0 w-1/4 min-w-[360px] max-w-[460px] flex flex-col z-40 bg-white/95 dark:bg-neutral-900/98 backdrop-blur border-l border-neutral-200 dark:border-neutral-800">
      <AssetLibraryControls
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sourceFilter={sourceFilter}
        onFilterChange={handleFilterChange}
        showGrouped={showGrouped}
        setShowGrouped={setShowGrouped}
        onClose={onClose}
      />

      {targetChannel && (
        <div className="px-5 py-2 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800 text-sm font-medium text-primary-700 dark:text-primary-300 truncate">
          {t('library.panel.addingTo', { name: targetChannel.name })}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
          </div>
        )}
        {!isLoading && filteredAssets.length === 0 && (
          <div className="text-center py-12 text-sm text-neutral-500 dark:text-neutral-400">{t('library.panel.empty')}</div>
        )}
        {!isLoading && filteredAssets.length > 0 && showGrouped && (
          <div className="space-y-5">
            {Array.from(groupedAssets.entries()).map(([key, group]) => (
              <SessionGroup
                key={key}
                sessionKey={key}
                dateLabel={group.dateLabel}
                timeLabel={group.timeLabel}
                sessionLabel={group.sessionLabel}
                assets={group.assets}
                isExpanded={expandedSessions.has(key)}
                onToggle={() => toggleSession(key)}
                productId={productId}
                onAdd={onAdd}
              />
            ))}
          </div>
        )}
        {!isLoading && filteredAssets.length > 0 && !showGrouped && (
          <div className="grid grid-cols-2 gap-3">
            {filteredAssets.map((asset) => (
              <DraggableAssetThumbnail key={asset.asset_id} asset={asset} productId={productId} onAdd={onAdd} />
            ))}
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <span className="text-sm text-neutral-500 dark:text-neutral-400">{t('library.panel.count', { count: totalCount })}</span>
        <div className="flex items-center gap-2">
          <PagerButton disabled={page === 1 || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="w-4 h-4" />
          </PagerButton>
          <span className="text-sm text-neutral-500 dark:text-neutral-400 px-2">{page}/{totalPages}</span>
          <PagerButton disabled={page === totalPages || isFetching} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="w-4 h-4" />
          </PagerButton>
        </div>
      </div>
    </aside>
  );
}

function PagerButton({ disabled, onClick, children }: { disabled: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-8 h-8 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
    >
      {children}
    </button>
  );
}
